import { NavLink, Outlet } from "react-router-dom";
import { House, Package, Sparkle, ChatCircleDots, Gear, Lightning, Storefront, SignOut, ChartLine } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const navItems = [
  { to: "/", label: "OVERVIEW", icon: House, testId: "nav-overview" },
  { to: "/orders", label: "ORDERS", icon: Package, testId: "nav-orders" },
  { to: "/products", label: "PRODUCTS", icon: Storefront, testId: "nav-products" },
  { to: "/analytics", label: "ANALYTICS", icon: ChartLine, testId: "nav-analytics" },
  { to: "/ads", label: "AD FORGE", icon: Sparkle, testId: "nav-ads" },
  { to: "/chatbot", label: "CHATBOT", icon: ChatCircleDots, testId: "nav-chatbot" },
  { to: "/settings", label: "SETTINGS", icon: Gear, testId: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-64 border-r border-arena-border bg-arena-bg flex-shrink-0 flex flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-arena-border">
          <div className="flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-arena-primary" />
            <span className="font-heading font-bold text-arena-text tracking-tight text-lg">PEP<span className="text-arena-primary">.</span>STYLES</span>
          </div>
          <p className="overline mt-2">e-com command center</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-xs tracking-widest transition-colors duration-150 ${
                  isActive
                    ? "bg-arena-primary text-black font-bold"
                    : "text-arena-text hover:bg-arena-surfaceHover hover:text-arena-primary"
                }`
              }
            >
              <Icon size={16} weight="bold" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User + Logout */}
        {user && (
          <div className="p-4 border-t border-arena-border" data-testid="user-panel">
            <div className="flex items-center gap-2 mb-3">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 border border-arena-border object-cover" />
              ) : (
                <div className="w-8 h-8 bg-arena-primary text-black flex items-center justify-center font-heading text-xs font-bold">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs truncate">{user.name || "Owner"}</div>
                <div className="text-[9px] text-arena-muted truncate">{user.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="w-full border border-arena-border px-3 py-2 text-[10px] tracking-widest uppercase hover:border-arena-danger hover:text-arena-danger flex items-center justify-center gap-2"
            >
              <SignOut size={12} weight="bold" />
              Sign out
            </button>
          </div>
        )}

        <div className="p-4 border-t border-arena-border">
          <div className="overline">system status</div>
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="w-2 h-2 bg-arena-success animate-pulse-dot" />
            <span className="text-arena-success">ONLINE</span>
            <span className="text-arena-muted ml-auto">v1.2</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
